import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

const BITVAVO_REST_BASE = 'https://api.bitvavo.com/v2';
const BITVAVO_WS_URL = 'wss://ws.bitvavo.com/v2';
const BITVAVO_MAX_CANDLES = 1440;
const BITVAVO_MAX_TRADES = 100;
const BITVAVO_BOOK_DEPTH = 1000;
const BITVAVO_MAX_BUFFERED_BOOK_EVENTS = 5000;
const BITVAVO_RENDER_INTERVAL_MS = 100;
const BITVAVO_RESYNC_DELAY_MS = 250;

export const bitvavoRestClient = axios;
export const bitvavoProxyRestClient = proxyRestOptions.client!;

type BitvavoNumber = number | string;
type BitvavoBookLevel = [price: BitvavoNumber, size: BitvavoNumber];

interface BitvavoMarket {
  market: string;
  status: string;
  quantityDecimals: number;
  tickSize: BitvavoNumber;
}

interface BitvavoTicker {
  market: string;
  timestamp: number;
  open: BitvavoNumber;
  high: BitvavoNumber;
  low: BitvavoNumber;
  last: BitvavoNumber;
  bid: BitvavoNumber;
  bidSize: BitvavoNumber;
  ask: BitvavoNumber;
  askSize: BitvavoNumber;
  volume: BitvavoNumber;
  volumeQuote: BitvavoNumber;
}

export interface BitvavoOrderbookSnapshot {
  market: string;
  nonce: number;
  timestamp: BitvavoNumber;
  bids: BitvavoBookLevel[];
  asks: BitvavoBookLevel[];
}

export interface BitvavoBookUpdate {
  event: 'book';
  market: string;
  nonce: number;
  timestamp?: BitvavoNumber;
  bids?: BitvavoBookLevel[];
  asks?: BitvavoBookLevel[];
}

interface BitvavoTrade {
  id: BitvavoNumber;
  timestamp: number;
  amount: BitvavoNumber;
  price: BitvavoNumber;
  side: string;
}

export type BitvavoCandle = [
  timestamp: BitvavoNumber,
  open: BitvavoNumber,
  high: BitvavoNumber,
  low: BitvavoNumber,
  close: BitvavoNumber,
  volume: BitvavoNumber,
];

interface BitvavoWsMessage {
  event?: 'subscribed' | 'book' | 'trade' | 'ticker' | 'candle' | 'error';
  market?: string;
  nonce?: number;
  timestamp?: BitvavoNumber;
  timestampNs?: number;
  bids?: BitvavoBookLevel[];
  asks?: BitvavoBookLevel[];
  id?: BitvavoNumber;
  amount?: BitvavoNumber;
  price?: BitvavoNumber;
  side?: string;
  bestBid?: BitvavoNumber;
  bestBidSize?: BitvavoNumber;
  bestAsk?: BitvavoNumber;
  bestAskSize?: BitvavoNumber;
  open?: BitvavoNumber;
  high?: BitvavoNumber;
  low?: BitvavoNumber;
  last?: BitvavoNumber;
  volume?: BitvavoNumber;
  volumeQuote?: BitvavoNumber;
  interval?: string;
  candle?: BitvavoCandle[];
  errorCode?: number;
  error?: string;
}

const BITVAVO_TIMEFRAMES: Partial<Record<IChartTimeframe, string>> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '2h',
  '4h': '4h',
  '6h': '6h',
  '8h': '8h',
  '12h': '12h',
  '24h': '1d',
  '1d': '1d',
  '168h': '1W',
  '1w': '1W',
  '1M': '1M',
};

const BITVAVO_INTERVAL_BY_MILLISECONDS: Record<number, string> = {
  60_000: '1m',
  300_000: '5m',
  900_000: '15m',
  1_800_000: '30m',
  3_600_000: '1h',
  7_200_000: '2h',
  14_400_000: '4h',
  21_600_000: '6h',
  28_800_000: '8h',
  43_200_000: '12h',
  86_400_000: '1d',
  604_800_000: '1W',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown) => Number.isFinite(Number(value));
const asString = (value: BitvavoNumber) => String(value);

const normalizeBookLevels = (levels: BitvavoBookLevel[] | undefined): string[][] =>
  (levels ?? [])
    .filter(
      level =>
        Array.isArray(level) &&
        level.length >= 2 &&
        isFiniteNumber(level[0]) &&
        isFiniteNumber(level[1]),
    )
    .map(([price, size]) => [asString(price), asString(size)]);

const readFilteredItem = <T extends { market: string }>(
  payload: T | T[],
  market: string,
  label: string,
): T => {
  const items = Array.isArray(payload) ? payload : [payload];
  const item = items.find(candidate => candidate?.market === market);
  if (!item) throw new Error(`Bitvavo ${label} ${market} is unavailable`);
  return item;
};

const getBitvavo = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  try {
    return await bitvavoRestClient.get<T>(url, config);
  } catch (directError) {
    if (!bitvavoProxyRestClient) throw directError;
    return bitvavoProxyRestClient.get<T>(url, config);
  }
};

export const buildBitvavoMarketSymbol = (base: string, quote: string) =>
  `${base.toUpperCase()}-${quote.toUpperCase()}`;

export const normalizeBitvavoCandles = (candles: BitvavoCandle[]): KLine[] =>
  candles
    .filter(candle => Array.isArray(candle) && candle.length >= 6 && candle.every(isFiniteNumber))
    .map(
      ([timestamp, open, high, low, close, volume]) =>
        [
          Number(timestamp),
          asString(open),
          asString(high),
          asString(low),
          asString(close),
          asString(volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const bitvavoPrecisionFromTick = (tick: BitvavoNumber | undefined): number => {
  if (tick === undefined || !Number.isFinite(Number(tick)) || Number(tick) <= 0) return 1;

  const [, decimals = ''] = String(tick).split('.');
  return decimals.replace(/0+$/, '').length;
};

export const buildBitvavoSubscription = (market: string, interval: string) => ({
  action: 'subscribe' as const,
  channels: [
    { name: 'book' as const, markets: [market] },
    { name: 'trades' as const, markets: [market] },
    { name: 'ticker' as const, markets: [market] },
    { name: 'candles' as const, markets: [market], interval: [interval] },
  ],
});

export interface BitvavoOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
  nonce: number | undefined;
  synchronized: boolean;
}

export const createBitvavoOrderbookState = (): BitvavoOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
  nonce: undefined,
  synchronized: false,
});

const applyBookLevels = (target: Map<string, string>, levels: BitvavoBookLevel[] | undefined) => {
  normalizeBookLevels(levels).forEach(([price, size]) => {
    if (Number(size) === 0) {
      target.delete(price);
    } else {
      target.set(price, size);
    }
  });
};

export const applyBitvavoOrderbookSnapshot = (
  state: BitvavoOrderbookState,
  snapshot: BitvavoOrderbookSnapshot,
) => {
  if (!Number.isSafeInteger(snapshot.nonce) || snapshot.nonce <= 0) {
    throw new Error('Bitvavo orderbook snapshot has an invalid nonce');
  }
  state.bids.clear();
  state.asks.clear();
  applyBookLevels(state.bids, snapshot.bids);
  applyBookLevels(state.asks, snapshot.asks);
  state.nonce = snapshot.nonce;
  state.synchronized = true;
};

export type BitvavoBookApplyResult = 'applied' | 'stale' | 'gap';

export const applyBitvavoBookUpdate = (
  state: BitvavoOrderbookState,
  update: BitvavoBookUpdate,
): BitvavoBookApplyResult => {
  if (!state.synchronized || state.nonce === undefined) return 'gap';
  if (!Number.isSafeInteger(update.nonce) || update.nonce <= 0) return 'gap';
  if (update.nonce <= state.nonce) return 'stale';
  if (update.nonce !== state.nonce + 1) return 'gap';

  applyBookLevels(state.bids, update.bids);
  applyBookLevels(state.asks, update.asks);
  state.nonce = update.nonce;
  return 'applied';
};

export class BitvavoStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  orderbook = createBitvavoOrderbookState();
  bookBuffer: BitvavoBookUpdate[] = [];
  bookSyncPromise: Promise<void> | undefined;
  bookSyncGeneration = 0;
  bookSyncRetryTimer: ReturnType<typeof setTimeout> | undefined;
  orderbookPublishTimer: ReturnType<typeof setTimeout> | undefined;
  lastTicker: ITickerSocket | undefined;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildBitvavoMarketSymbol(base, quote);
    this.displayDepth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const asks = [...this.orderbook.asks.entries()]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.displayDepth);
    const bids = [...this.orderbook.bids.entries()]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.displayDepth);
    this.onOrderbookUpdate({ asks, bids });
  };

  scheduleOrderbookPublish = () => {
    if (this.orderbookPublishTimer) return;
    this.orderbookPublishTimer = setTimeout(() => {
      this.orderbookPublishTimer = undefined;
      this.publishOrderbook();
    }, BITVAVO_RENDER_INTERVAL_MS);
  };

  loadOrderbookSnapshot = async (): Promise<BitvavoOrderbookSnapshot> => {
    const response = await getBitvavo<BitvavoOrderbookSnapshot>(
      `${BITVAVO_REST_BASE}/${this.pair}/book`,
      { params: { depth: BITVAVO_BOOK_DEPTH } },
    );
    const snapshot = response.data;
    if (
      !isRecord(snapshot) ||
      snapshot.market !== this.pair ||
      !Number.isSafeInteger(snapshot.nonce) ||
      !Array.isArray(snapshot.bids) ||
      !Array.isArray(snapshot.asks)
    ) {
      throw new Error(`Bitvavo orderbook ${this.pair} has an invalid shape`);
    }
    return snapshot;
  };

  scheduleBookResync = () => {
    if (this.bookSyncRetryTimer || !this.ws) return;
    this.bookSyncRetryTimer = setTimeout(() => {
      this.bookSyncRetryTimer = undefined;
      void this.synchronizeOrderbook();
    }, BITVAVO_RESYNC_DELAY_MS);
  };

  synchronizeOrderbook = (): Promise<void> => {
    if (this.bookSyncPromise) return this.bookSyncPromise;
    const generation = this.bookSyncGeneration;

    const sync = (async () => {
      const snapshot = await this.loadOrderbookSnapshot();
      if (generation !== this.bookSyncGeneration || !this.ws) return;

      applyBitvavoOrderbookSnapshot(this.orderbook, snapshot);
      const buffered = [...this.bookBuffer]
        .filter(update => update.nonce > snapshot.nonce)
        .sort((left, right) => left.nonce - right.nonce);
      this.bookBuffer = [];

      for (let index = 0; index < buffered.length; index += 1) {
        const result = applyBitvavoBookUpdate(this.orderbook, buffered[index]);
        if (result === 'gap') {
          this.orderbook.synchronized = false;
          this.bookBuffer = buffered.slice(index);
          throw new Error(
            `Bitvavo orderbook nonce gap after ${this.orderbook.nonce ?? snapshot.nonce}`,
          );
        }
      }
      this.publishOrderbook();
    })();

    this.bookSyncPromise = sync;
    void sync
      .catch(error => {
        if (generation === this.bookSyncGeneration && this.ws) {
          console.log('Bitvavo orderbook synchronization error:', error);
          this.scheduleBookResync();
        }
      })
      .finally(() => {
        if (this.bookSyncPromise === sync) this.bookSyncPromise = undefined;
      });
    return sync;
  };

  handleBookUpdate = (update: BitvavoBookUpdate) => {
    if (!this.orderbook.synchronized || this.orderbook.nonce === undefined) {
      this.bookBuffer.push(update);
      if (this.bookBuffer.length > BITVAVO_MAX_BUFFERED_BOOK_EVENTS) {
        this.bookBuffer = this.bookBuffer.slice(-BITVAVO_MAX_BUFFERED_BOOK_EVENTS);
      }
      return;
    }

    const result = applyBitvavoBookUpdate(this.orderbook, update);
    if (result === 'applied') {
      this.scheduleOrderbookPublish();
      return;
    }
    if (result === 'gap') {
      this.orderbook.synchronized = false;
      this.bookBuffer = [update];
      void this.synchronizeOrderbook();
    }
  };

  publishTicker = (ticker: BitvavoTicker) => {
    const lastNumber = Number(ticker.last);
    const openNumber = Number(ticker.open);
    if (
      ticker.market !== this.pair ||
      !Number.isFinite(lastNumber) ||
      !Number.isFinite(openNumber) ||
      !isFiniteNumber(ticker.high) ||
      !isFiniteNumber(ticker.low) ||
      !isFiniteNumber(ticker.volume) ||
      !isFiniteNumber(ticker.volumeQuote)
    ) {
      throw new Error(`Bitvavo ticker ${this.pair} has an invalid shape`);
    }
    const change = openNumber === 0 ? 0 : ((lastNumber - openNumber) / openNumber) * 100;
    this.lastTicker = {
      baseVolume: asString(ticker.volume),
      change: normalizeNumber(change),
      close: asString(ticker.last),
      high: asString(ticker.high),
      last: asString(ticker.last),
      low: asString(ticker.low),
      quoteVolume: asString(ticker.volumeQuote),
    };
    this.onTickerUpdate(this.lastTicker);
  };

  publishTradeTicker = (price: string) => {
    this.lastTicker = {
      baseVolume: this.lastTicker?.baseVolume ?? '',
      change: this.lastTicker?.change ?? '',
      close: price,
      high: this.lastTicker?.high ?? '',
      last: price,
      low: this.lastTicker?.low ?? '',
      quoteVolume: this.lastTicker?.quoteVolume ?? '',
    };
    this.onTickerUpdate(this.lastTicker);
  };

  publishPartialTicker = (message: BitvavoWsMessage) => {
    if (
      message.last === undefined &&
      message.high === undefined &&
      message.low === undefined &&
      message.volume === undefined &&
      message.volumeQuote === undefined
    ) {
      return;
    }
    const last = message.last === undefined ? this.lastTicker?.last : asString(message.last);
    if (!last) return;
    const open = message.open === undefined ? undefined : Number(message.open);
    const lastNumber = Number(last);
    const change =
      open !== undefined && Number.isFinite(open) && open !== 0 && Number.isFinite(lastNumber)
        ? normalizeNumber(((lastNumber - open) / open) * 100)
        : (this.lastTicker?.change ?? '');
    this.lastTicker = {
      baseVolume:
        message.volume === undefined
          ? (this.lastTicker?.baseVolume ?? '')
          : asString(message.volume),
      change,
      close: last,
      high: message.high === undefined ? (this.lastTicker?.high ?? '') : asString(message.high),
      last,
      low: message.low === undefined ? (this.lastTicker?.low ?? '') : asString(message.low),
      quoteVolume:
        message.volumeQuote === undefined
          ? (this.lastTicker?.quoteVolume ?? '')
          : asString(message.volumeQuote),
    };
    this.onTickerUpdate(this.lastTicker);
  };

  fetchMarket = async () => {
    try {
      const response = await getBitvavo<BitvavoMarket | BitvavoMarket[]>(
        `${BITVAVO_REST_BASE}/markets`,
        { params: { market: this.pair } },
      );
      const market = readFilteredItem(response.data, this.pair, 'market');
      if (market.status.toLowerCase() !== 'trading' || !isFiniteNumber(market.tickSize)) {
        throw new Error(`Bitvavo market ${this.pair} is unavailable`);
      }
      this.priceTick = asString(market.tickSize);
      this.precision = bitvavoPrecisionFromTick(market.tickSize);
      this.selectedPrecision = this.precision;
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Bitvavo market error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const snapshot = await this.loadOrderbookSnapshot();
      applyBitvavoOrderbookSnapshot(this.orderbook, snapshot);
      this.publishOrderbook();
    } catch (error) {
      console.log('Bitvavo orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await getBitvavo<BitvavoTicker | BitvavoTicker[]>(
        `${BITVAVO_REST_BASE}/ticker/24h`,
        { params: { market: this.pair } },
      );
      this.publishTicker(readFilteredItem(response.data, this.pair, 'ticker'));
    } catch (error) {
      console.log('Bitvavo ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const bitvavoInterval = BITVAVO_TIMEFRAMES[interval];
    if (!bitvavoInterval) return [];
    const boundedLimit = Math.min(BITVAVO_MAX_CANDLES, Math.max(1, limit));
    const end = to > 0 ? to - 1 : Date.now();
    const start = from > 0 ? from : undefined;

    try {
      const response = await getBitvavo<BitvavoCandle[]>(
        `${BITVAVO_REST_BASE}/${this.pair}/candles`,
        {
          params: {
            end,
            interval: bitvavoInterval,
            limit: boundedLimit,
            start,
          },
        },
      );
      return normalizeBitvavoCandles(response.data ?? [])
        .filter(row => (from <= 0 || row[0] >= from) && (to <= 0 || row[0] < to))
        .slice(-boundedLimit);
    } catch (error) {
      console.log('Bitvavo klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    const boundedLimit = Math.min(BITVAVO_MAX_TRADES, Math.max(1, limit));
    try {
      const response = await getBitvavo<BitvavoTrade[]>(
        `${BITVAVO_REST_BASE}/${this.pair}/trades`,
        { params: { limit: boundedLimit } },
      );
      if (!Array.isArray(response.data)) {
        throw new Error(`Bitvavo trades ${this.pair} have an invalid shape`);
      }
      return response.data
        .filter(
          trade =>
            isFiniteNumber(trade.timestamp) &&
            isFiniteNumber(trade.price) &&
            isFiniteNumber(trade.amount),
        )
        .map(trade => ({
          price: asString(trade.price),
          quantity: asString(trade.amount),
          timestamp: String(trade.timestamp),
          side: trade.side.toLowerCase(),
        }));
    } catch (error) {
      console.log('Bitvavo trades error:', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    const candleInterval = BITVAVO_INTERVAL_BY_MILLISECONDS[interval] ?? '1m';
    const ws = new ReconnectingWebSocket(BITVAVO_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;

    ws.onopen = () => {
      this.bookSyncGeneration += 1;
      this.bookSyncPromise = undefined;
      if (this.bookSyncRetryTimer) {
        clearTimeout(this.bookSyncRetryTimer);
        this.bookSyncRetryTimer = undefined;
      }
      this.orderbook.bids.clear();
      this.orderbook.asks.clear();
      this.orderbook.nonce = undefined;
      this.orderbook.synchronized = false;
      this.bookBuffer = [];
      ws.send(JSON.stringify(buildBitvavoSubscription(this.pair, candleInterval)));
      void this.synchronizeOrderbook();
    };

    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        const message = JSON.parse(event.data) as BitvavoWsMessage;
        if (message.event === 'subscribed') return;
        if (message.event === 'error') {
          console.log('Bitvavo websocket subscription error:', message.errorCode, message.error);
          return;
        }
        if (message.market !== this.pair) return;

        if (message.event === 'book' && Number.isSafeInteger(message.nonce)) {
          this.handleBookUpdate({
            event: 'book',
            market: this.pair,
            nonce: message.nonce!,
            timestamp: message.timestamp,
            bids: message.bids,
            asks: message.asks,
          });
          return;
        }
        if (
          message.event === 'trade' &&
          message.price !== undefined &&
          message.amount !== undefined &&
          message.timestamp !== undefined &&
          message.side
        ) {
          const price = asString(message.price);
          this.onTradeUpdate({
            price,
            quantity: asString(message.amount),
            timestamp: String(message.timestamp),
            side: message.side.toLowerCase(),
          });
          this.publishTradeTicker(price);
          return;
        }
        if (message.event === 'ticker') {
          this.publishPartialTicker(message);
          return;
        }
        if (message.event === 'candle' && Array.isArray(message.candle)) {
          normalizeBitvavoCandles(message.candle).forEach(candle => this.klineUpdate(candle));
        }
      } catch (error) {
        console.log('Bitvavo websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
    this.bookSyncGeneration += 1;
    this.bookSyncPromise = undefined;
    this.bookBuffer = [];
    this.orderbook.synchronized = false;
    if (this.bookSyncRetryTimer) {
      clearTimeout(this.bookSyncRetryTimer);
      this.bookSyncRetryTimer = undefined;
    }
    if (this.orderbookPublishTimer) {
      clearTimeout(this.orderbookPublishTimer);
      this.orderbookPublishTimer = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchMarket(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new BitvavoStrategy(args);
    await bootstrapStrategy(strategy);
    return strategy;
  };
}
